import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { RoomType, RoomTypeCreate, RoomTypeUpdate } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  name: z.string().min(1, "타입명을 입력하세요"),
  description: z.string().optional(),
  base_rate: z.string().min(1, "기본요금을 입력하세요"),
  capacity: z.string().optional(),
  is_active: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface RoomTypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog edits an existing room type; otherwise creates one. */
  roomType?: RoomType | null;
  isPending?: boolean;
  onSubmit: (values: RoomTypeCreate | RoomTypeUpdate) => void;
}

export function RoomTypeFormDialog({
  open,
  onOpenChange,
  roomType,
  isPending,
  onSubmit,
}: RoomTypeFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // Reset the form whenever the dialog opens (new type vs. edit).
  useEffect(() => {
    if (open) {
      reset({
        name: roomType?.name ?? "",
        description: roomType?.description ?? "",
        base_rate: roomType?.base_rate != null ? String(roomType.base_rate) : "",
        capacity: roomType?.capacity != null ? String(roomType.capacity) : "",
        is_active: roomType?.is_active ?? true,
      });
    }
  }, [open, roomType, reset]);

  const submit = handleSubmit((values) => {
    const base_rate = Number.parseFloat(values.base_rate);
    const payload: RoomTypeCreate | RoomTypeUpdate = {
      name: values.name.trim(),
      base_rate: Number.isNaN(base_rate) ? 0 : base_rate,
    };
    if (values.description?.trim()) payload.description = values.description.trim();
    if (values.capacity?.trim()) {
      const n = Number.parseInt(values.capacity, 10);
      if (!Number.isNaN(n)) payload.capacity = n;
    }
    // is_active is only relevant for edit mode
    if (roomType) {
      (payload as RoomTypeUpdate).is_active = values.is_active;
    }
    onSubmit(payload);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{roomType ? "객실 타입 수정" : "새 객실 타입"}</DialogTitle>
          <DialogDescription>
            객실 타입별 기본요금과 정원을 설정하면 예약 관리에 활용됩니다.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="rt-name">타입명 *</Label>
            <Input id="rt-name" {...register("name")} placeholder="스탠다드 더블" />
            {errors.name ? <p className="text-xs text-destructive">{errors.name.message}</p> : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rt-description">설명</Label>
            <Textarea
              id="rt-description"
              {...register("description")}
              placeholder="오션뷰 스탠다드 더블룸"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="rt-base-rate">1박 기본요금 (₩) *</Label>
              <Input
                id="rt-base-rate"
                type="number"
                min={0}
                step={1000}
                {...register("base_rate")}
                placeholder="150000"
              />
              {errors.base_rate ? (
                <p className="text-xs text-destructive">{errors.base_rate.message}</p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rt-capacity">정원</Label>
              <Input
                id="rt-capacity"
                type="number"
                min={1}
                {...register("capacity")}
                placeholder="2"
              />
            </div>
          </div>
          {roomType ? (
            <div className="flex items-center justify-between rounded-lg border border-border/70 px-3 py-2.5">
              <Label htmlFor="rt-is-active">활성 상태</Label>
              <Controller
                control={control}
                name="is_active"
                render={({ field }) => (
                  <Switch
                    id="rt-is-active"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              취소
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {roomType ? "저장" : "등록"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
